import React from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSection  from '../components/HeroSection';
import ContactForm  from '../components/ContactForm';
import Reveal       from '../components/Reveal';

const FAQ = [
  {
    q: 'What types of inquiries does Driven 2 LLC accept?',
    a: 'We welcome inquiries related to brand partnerships, acquisition or investment discussions, media requests, general business questions, and opportunities relevant to our portfolio brands. We review all submissions and respond to substantive inquiries directly.',
  },
  {
    q: 'How quickly can I expect a response?',
    a: 'We aim to respond to all substantive inquiries within 1–2 business days. Complex requests — particularly those related to acquisition discussions or formal partnership proposals — may take additional time to evaluate properly before we respond.',
  },
  {
    q: 'How should I approach an acquisition or investment inquiry?',
    a: 'Select "Investment / Acquisition Inquiry" in the form and provide relevant context about your interest, the specific brand or the portfolio as a whole, and the nature of your inquiry. We will respond directly to evaluate the opportunity.',
  },
  {
    q: 'I want to discuss a specific portfolio brand. Who should I contact?',
    a: 'All inquiries related to specific portfolio brands — DriveTalkAI, AddyMay, Driven2Win, or PIC and SAV — should be directed to the holding company via this form or at info@driven2.net. We will route your inquiry appropriately.',
  },
];

export default function Contact() {
  return (
    <>
      <Helmet>
        <title>Contact Driven 2 LLC — Business Inquiries and Partnerships</title>
        <meta
          name="description"
          content="Contact Driven 2 LLC for partnership discussions, investment and acquisition inquiries, media requests, and general business questions. We welcome substantive conversations."
        />
        <meta property="og:title" content="Contact Driven 2 LLC — Business Inquiries and Partnerships" />
        <meta property="og:description" content="Reach Driven 2 LLC for partnership, acquisition, or investment discussions. Florida · 904-638-7199 · info@driven2.net" />
        <meta property="og:url" content="https://www.driven2.net/contact" />
        <link rel="canonical" href="https://www.driven2.net/contact" />
      </Helmet>

      {/*
        ═══════════════════════════════════════════
        HERO — MIX: LIGHTER OVERLAY / WARM TONE
        PLACEHOLDER DIRECTION:
        "Elegant modern office environment with
        floor-to-ceiling windows, city view,
        refined architectural lines, warm natural
        light from the side. Subtle dark overlay —
        slightly less opaque than other pages for
        a more welcoming, approachable feel."

        TO REPLACE: add bgImage="/images/hero-contact.jpg"
        ═══════════════════════════════════════════
      */}
      <HeroSection
        eyebrow="Contact Driven 2 LLC"
        title="Begin a Conversation"
        subtitle="We engage with serious counterparts — partners, investors, brand founders, and collaborators who share our orientation toward long-term value creation."
        size="small"
        align="center"
        bgImage="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1920&q=80"
      />

      {/* ─── CONTACT MAIN ─── */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.8fr', gap: 'clamp(2rem, 6vw, 6rem)', alignItems: 'start' }}>

            {/* Left: contact info */}
            <Reveal>
              <div className="eyebrow">Get in Touch</div>
              <h2 className="d-md t-navy mb-8">Direct Contact Information</h2>

              <div style={{ marginBottom: 'var(--sp-10)' }}>
                {[
                  { label: 'Phone',   value: '904-638-7199',           href: 'tel:9046387199' },
                  { label: 'Email',   value: 'info@driven2.net',           href: 'mailto:info@driven2.net' },
                  { label: 'Website', value: 'www.driven2.net',            href: 'https://www.driven2.net' },
                ].map(({ label, value, href }) => (
                  <div key={label} className="contact-info-item">
                    <div className="contact-info-label">{label}</div>
                    <div className="contact-info-value">
                      {href
                        ? <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noreferrer' : undefined}>{value}</a>
                        : value}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'var(--navy-900)', padding: 'var(--sp-6)', borderLeft: '2px solid var(--gold-500)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold-400)', marginBottom: 'var(--sp-3)' }}>
                  Response Commitment
                </div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.62)', lineHeight: 1.75 }}>
                  We review all inquiries and respond to substantive submissions within{' '}
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>1–2 business days</strong>.
                  For complex acquisition or investment discussions, initial acknowledgment
                  is provided promptly while we conduct a thorough internal review.
                </p>
              </div>

              <div style={{ marginTop: 'var(--sp-10)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gray-500)', marginBottom: 'var(--sp-4)' }}>
                  Inquiry Types We Accept
                </div>
                {[
                  'General business inquiries',
                  'Brand partnership discussions',
                  'Investment and acquisition conversations',
                  'Media and press requests',
                  'Portfolio brand-specific inquiries',
                ].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', fontSize: 'var(--text-sm)', color: 'var(--gray-700)', marginBottom: 'var(--sp-3)' }}>
                    <div style={{ width: 6, height: 6, background: 'var(--gold-500)', flexShrink: 0 }} />
                    {item}
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Right: form */}
            <Reveal delay={0.1}>
              <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', padding: 'clamp(1.5rem, 4vw, 2.5rem)' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-2xl)', fontWeight: 400, color: 'var(--navy-800)', marginBottom: 'var(--sp-2)' }}>
                  Submit an Inquiry
                </h3>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-500)', lineHeight: 1.7, marginBottom: 'var(--sp-8)' }}>
                  All fields marked with an asterisk are required. We treat all inquiries
                  with confidentiality and professional discretion.
                </p>
                <ContactForm />
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="section section--alt section--rule">
        <div className="container">
          <Reveal>
            <div className="section-intro">
              <div className="eyebrow">FAQ</div>
              <h2 className="d-md t-navy">Common Questions</h2>
            </div>
          </Reveal>

          <div style={{ maxWidth: '860px' }}>
            {FAQ.map(({ q, a }, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div style={{ padding: 'var(--sp-8) 0', borderBottom: '1px solid var(--gray-200)' }}>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 500, color: 'var(--navy-800)', marginBottom: 'var(--sp-3)' }}>
                    {q}
                  </h3>
                  <p style={{ fontSize: 'var(--text-base)', color: 'var(--gray-600)', lineHeight: 1.8 }}>{a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
